import { useLayoutEffect, useState } from 'react';

// Clearance kept between the panel and the viewport edges.
const GUTTER = 16;
// Gap between the trigger and the panel.
const OFFSET = 6;

const HIDDEN = { left: 0, top: 0, visibility: 'hidden' };

// The app's fixed top bar, tagged with a data attribute so this hook doesn't have to import from the
// navigation module. Returns 0 when there is no fixed chrome (e.g. the public pages).
const topChromeBottom = () => {
  const header = document.querySelector('[data-app-header]');
  return header ? header.getBoundingClientRect().bottom : 0;
};

/**
 * Positions a popover panel against its trigger in **viewport** coordinates, for panels rendered through
 * a portal with `position: fixed`.
 *
 * Why a portal rather than `position: absolute` on the trigger's wrapper: an absolutely-positioned panel
 * is clipped by any ancestor with `overflow: hidden|auto|scroll` and trapped in its stacking context, so
 * it gets sliced off inside cards, scroll areas and media tiles — and which ancestor does the clipping
 * differs per page. Portalling to <body> sidesteps all of it; the cost is that fixed coordinates must be
 * recomputed whenever the trigger moves, which is what this hook owns.
 *
 * Behaviour:
 *   - aligns the panel's left (or right, via `align`) edge to the trigger's
 *   - clamps it into [gutter, viewportWidth - width - gutter] so neither edge can leave the viewport; on
 *     a viewport narrower than the panel that range collapses and the panel sits centred between gutters
 *   - flips above the trigger when there isn't room below
 *   - follows the trigger on scroll/resize instead of closing, ignoring scrolls that originate inside the
 *     panel itself (the trigger hasn't moved, so re-rendering on every wheel tick would only cause jank)
 *
 * Returns a style object to spread onto the panel. It starts hidden and is corrected in a layout effect,
 * so the panel is never painted in the wrong place.
 */
const useAnchoredPanel = ({ open, triggerRef, panelRef, align = 'left', gutter = GUTTER }) => {
  const [style, setStyle] = useState(HIDDEN);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(HIDDEN);
      return undefined;
    }

    const place = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;

      const rect = trigger.getBoundingClientRect();
      // Measured, not assumed: the panel is usually also capped by a `max-width` on narrow viewports.
      const { offsetWidth: width, offsetHeight: height } = panel;

      const preferredLeft = align === 'right' ? rect.right - width : rect.left;
      const maxLeft = Math.max(gutter, window.innerWidth - width - gutter);
      const left = Math.min(Math.max(preferredLeft, gutter), maxLeft);

      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < height + gutter && rect.top > spaceBelow;
      const position = openUp
        ? { left, bottom: window.innerHeight - rect.top + OFFSET }
        : { left, top: rect.bottom + OFFSET };

      // Where the panel's own top edge lands, whichever way it opened.
      const panelTop = openUp ? window.innerHeight - position.bottom - height : position.top;
      // Keep the coordinates so it pops back into place on scrolling down again, rather than re-measuring
      // from scratch.
      setStyle(panelTop < topChromeBottom() ? { ...position, visibility: 'hidden' } : position);
    };

    place();

    const reposition = (event) => {
      // `instanceof Node` is required, not defensive: a resize event's target is `window`, and
      // Node.contains() throws a TypeError on anything that isn't a Node.
      const target = event?.target;
      if (target instanceof Node && panelRef.current?.contains(target)) return;
      place();
    };
    // Capture phase so scrolling of any ancestor container counts, not just the window.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, triggerRef, panelRef, align, gutter]);

  return style;
};

export default useAnchoredPanel;
